import { useState } from "react";
import {
  Alert, Box, MenuItem, Paper, Stack, TextField,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { shipmentsApi, type ShipmentListDto, type ShipmentStatus } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill, type PillTone } from "@/components/StatusPill";
import { formatDate } from "@/shared/datetime";

const STATUSES: (ShipmentStatus | "")[] = ["", "Planned", "Loaded", "Dispatched", "InTransit", "Arrived", "Delivered", "Closed", "Exception"];
const STATUS_TONE: Record<string, PillTone> = {
  Planned: "neutral", Loaded: "info", Dispatched: "warning", InTransit: "warning",
  Arrived: "info", Delivered: "success", Closed: "neutral", Exception: "critical",
};

export function ShipmentsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ShipmentStatus | "">("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const query = useQuery({
    queryKey: ["shipments", status, pagination.page, pagination.pageSize],
    queryFn: () => shipmentsApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<ShipmentListDto>[] = [
    { field: "transferOrderNo", headerName: "Transfer", width: 150 },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => <StatusPill label={p.value as string} tone={STATUS_TONE[p.value as string] ?? "neutral"} /> },
    { field: "carrierName", headerName: "Carrier", flex: 1, minWidth: 160,
      valueGetter: (_v, row) => row.carrierName ?? "—" },
    { field: "vehicleRegNo", headerName: "Vehicle", width: 140,
      valueGetter: (_v, row) => row.vehicleRegNo ?? "—" },
    { field: "driverName", headerName: "Driver", flex: 1, minWidth: 160,
      valueGetter: (_v, row) => row.driverName ?? "—" },
    { field: "requestedDeliveryDate", headerName: "Requested by", width: 130,
      valueFormatter: (v) => v ? formatDate(v as string) : "—" },
    { field: "actualDeliveryDate", headerName: "Delivered", width: 130,
      valueFormatter: (v) => v ? formatDate(v as string) : "—" },
    { field: "podStatus", headerName: "POD", width: 110,
      valueGetter: (_v, row) => row.podStatus ?? "—" },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Logistics", "Shipments"]} title="Shipments" />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value as ShipmentStatus | "")}
            size="small" sx={{ minWidth: 160 }}>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/transport/shipments/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
